import { type JSX, useEffect, useRef, useState } from "react"
import { loadBundle } from "../lazy"

// A read-only look at a program, syntax highlighted.
//
// The highlighting itself lives in a bundle of its own (app/javascript/
// code_viewer.ts) and is fetched the first time someone opens a job drawer.
// This side is the seam: a host element, a dynamic import, and a <pre> to fall
// back to. Nothing here imports CodeMirror, which is what keeps it out of
// application.js.

type Mount = (host: HTMLElement, options: { code: string; tool: string }) => () => void

function loadViewer(): Promise<Mount> {
  return loadBundle<{ mount: Mount }>("code-viewer").then((module) => module.mount)
}

export function CodeViewer({ code, tool }: { code: string; tool: string }): JSX.Element {
  const host = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let destroy: (() => void) | null = null
    let cancelled = false

    void loadViewer()
      .then((mount) => {
        if (cancelled || !host.current) return
        destroy = mount(host.current, { code, tool })
      })
      .catch(() => setFailed(true))

    return () => {
      cancelled = true
      destroy?.()
    }
  }, [code, tool])

  // Unhighlighted, but readable: a viewer that failed to load must not turn
  // the drawer into an empty box. The program is the point; the colours are a
  // convenience.
  if (failed) return <pre className="workspace__job-source workspace__job-source--plain">{code}</pre>

  return <div className="workspace__job-source" ref={host} />
}
